import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Mail } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StoreButtons from "../components/StoreButtons";
import useBreadcrumbJsonLd from "../lib/useBreadcrumbJsonLd";
import { LINKS } from "../lib/constants";
import { CATEGORIES } from "../data/faqCategories";

/* Inline link renderer: turns `[label](href)` inside an answer string
   into a styled <Link>. Kept in this file (not the data module) so
   the data module stays JSX-free and importable from Node build
   scripts (e.g. scripts/generate-llms-txt.mjs). */
const MD_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;

// Strip markdown-style `[label](href)` from an answer so it's safe to
// emit as a Schema.org "Answer.text" string. Internal links collapse
// to their label only; external URLs (DOIs, citations) preserve as
// "label (url)" so the JSON-LD carries the citation Google sees.
function stripMdLinks(text) {
    return text.replace(MD_LINK, (_, label, href) =>
        /^https?:\/\//i.test(href) ? `${label} (${href})` : label
    );
}

function renderAnswer(text) {
    const parts = [];
    let last = 0;
    let m;
    MD_LINK.lastIndex = 0;
    while ((m = MD_LINK.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        parts.push({ label: m[1], href: m[2] });
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    const linkClass =
        "text-[#49bef5] hover:text-white underline underline-offset-[3px] decoration-[#49bef5]/40 hover:decoration-white/60 transition-colors";
    return parts.map((p, i) => {
        if (typeof p === "string") return p;
        // External URLs (http(s)://...) open in a new tab via <a>.
        // Internal app paths route through React Router <Link> so
        // they preserve SPA navigation + scroll-to-hash behavior.
        const isExternal = /^https?:\/\//i.test(p.href);
        if (isExternal) {
            return (
                <a
                    key={i}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                >
                    {p.label}
                </a>
            );
        }
        return (
            <Link key={i} to={p.href} className={linkClass}>
                {p.label}
            </Link>
        );
    });
}

// Flatten ordered list of Q&A used by the FAQPage JSON-LD.
const FLAT_QA = CATEGORIES.flatMap((c) => c.items);

const FAQ_CRUMBS = [
    { name: "Home", url: "https://trtplus.app/" },
    { name: "FAQ", url: "https://trtplus.app/faq" },
];

/* Adds per-page meta + the FAQPage JSON-LD. Pulled out of the
   render so the cleanup runs on unmount and we don't leave stale
   schema in <head> if a user SPA-navigates away. */
function useFaqMeta() {
    useEffect(() => {
        // Scroll to top on mount — SPA navigation otherwise carries the
        // previous scroll position over, which is jarring when arriving
        // at a fresh page from a footer link halfway down the landing.
        window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });

        const prevTitle = document.title;
        document.title = "TRT Plus FAQ · Protocol, Steroid Plotter & Bloodwork Q&A";

        const descEl = document.querySelector('meta[name="description"]');
        const prevDesc = descEl?.getAttribute("content");
        if (descEl) {
            descEl.setAttribute(
                "content",
                "Answers to common questions about TRT Plus — protocol management, dose calculation, injection rotation, reminders, AI bloodwork analysis, Free T calculation, the Testimator, and more."
            );
        }

        const canonical = document.querySelector('link[rel="canonical"]');
        const prevCanonical = canonical?.getAttribute("href");
        if (canonical) canonical.setAttribute("href", "https://trtplus.app/faq");

        // Inject FAQPage JSON-LD. Put it in #root (not <head>) so the
        // prerender pass — which snapshots #root — picks it up. Google
        // parses ld+json from anywhere in the document.
        const faqLd = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FLAT_QA.map((qa) => ({
                "@type": "Question",
                name: qa.q,
                acceptedAnswer: { "@type": "Answer", text: stripMdLinks(qa.a) },
            })),
        };
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-faq", "true");
        script.textContent = JSON.stringify(faqLd);
        const host = document.getElementById("root") || document.head;
        host.appendChild(script);

        return () => {
            document.title = prevTitle;
            if (descEl && prevDesc) descEl.setAttribute("content", prevDesc);
            if (canonical && prevCanonical) canonical.setAttribute("href", prevCanonical);
            if (script.parentNode) script.parentNode.removeChild(script);
        };
    }, []);
}

function FaqItem({ qa, isOpen, onToggle, index }) {
    return (
        <div
            className="border-b border-white/8 group"
            data-testid={`faq-item-${index}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-start gap-6 py-6 lg:py-7 text-left transition-colors hover:bg-white/[0.015] -mx-4 px-4 rounded-lg"
                aria-expanded={isOpen}
                data-testid={`faq-trigger-${index}`}
            >
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#49bef5] pt-1 shrink-0 w-8">
                    {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="flex-1 font-display font-semibold text-white text-[17px] lg:text-[19px] leading-snug">
                    {qa.q}
                </h3>
                <ChevronDown
                    size={20}
                    className={`shrink-0 mt-1 text-white/45 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#49bef5]" : ""}`}
                />
            </button>
            <div
                className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
            >
                <div className="overflow-hidden">
                    <div
                        className="pb-7 pl-14 pr-10 text-white/70 text-[15px] leading-relaxed whitespace-pre-line"
                        data-testid={`faq-answer-${index}`}
                    >
                        {renderAnswer(qa.a)}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Faq() {
    useFaqMeta();
    useBreadcrumbJsonLd(FAQ_CRUMBS);
    // Track open items per global index so multiple sections can stay
    // open simultaneously — better for users scanning related answers.
    const [openSet, setOpenSet] = useState(() => new Set([0]));

    const toggle = (i) => {
        setOpenSet((prev) => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else next.add(i);
            return next;
        });
    };

    let runningIdx = -1;
    return (
        <main
            data-testid="faq-page"
            className="relative bg-black text-[#f4f5f7] font-body min-h-screen"
        >
            <Navbar />

            {/* Slim breadcrumb */}
            <div className="pt-24 lg:pt-28">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-6 pb-2">
                    <Link
                        to="/"
                        data-testid="faq-back-link"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-mono text-white/55 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to home
                    </Link>
                </div>
            </div>

            {/* Hero */}
            <section className="relative pt-10 pb-12 lg:pb-16 overflow-hidden">
                <div
                    className="absolute inset-0 grid-backdrop opacity-25 pointer-events-none"
                    aria-hidden
                />
                <div className="absolute inset-0 radial-glow opacity-50 pointer-events-none" />
                <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-6">
                        <span className="dot" /> Frequently Asked
                    </div>
                    <h1
                        className="font-display font-bold text-white leading-[1.02]"
                        style={{
                            fontSize: "clamp(2.5rem, 5.5vw, 4rem)",
                            letterSpacing: "-0.02em",
                        }}
                        data-testid="faq-title"
                    >
                        Straight answers.
                        <br />
                        <span className="blue-glow">No fluff.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-white/65 text-base lg:text-lg leading-relaxed">
                        If something isn't here, email us — every message
                        gets a personal reply from the developer.
                    </p>
                </div>
            </section>

            {/* FAQ body */}
            <section
                data-testid="faq-content"
                className="relative pb-20 lg:pb-28"
            >
                <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-14 lg:space-y-20">
                    {CATEGORIES.map((cat) => (
                        <div
                            key={cat.slug}
                            id={`category-${cat.slug}`}
                            data-testid={`faq-category-${cat.slug}`}
                            className="scroll-mt-32"
                        >
                            <div className="flex items-center gap-4 mb-2">
                                <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-[#49bef5]">
                                    {cat.label}
                                </div>
                                <div className="hairline flex-1" />
                            </div>
                            <div>
                                {cat.items.map((qa) => {
                                    runningIdx += 1;
                                    const i = runningIdx;
                                    return (
                                        <FaqItem
                                            key={qa.q}
                                            qa={qa}
                                            index={i}
                                            isOpen={openSet.has(i)}
                                            onToggle={() => toggle(i)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Contact + CTA */}
            <section
                data-testid="faq-cta"
                className="relative py-16 lg:py-20 border-t border-white/5"
            >
                <div className="max-w-4xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-[#49bef5] mb-3">
                            Still curious?
                        </div>
                        <h2 className="font-display font-bold text-white text-2xl lg:text-3xl leading-tight">
                            Best way to see it work is to{" "}
                            <span className="blue-glow">run it.</span>
                        </h2>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                        <StoreButtons testIdPrefix="faq" />
                    </div>
                </div>
                <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-10">
                    <a
                        href={LINKS.email}
                        data-testid="faq-email-link"
                        className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
                    >
                        <Mail size={15} /> support@trtplus.app
                    </a>
                </div>
            </section>

            <Footer />
        </main>
    );
}
